import { useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { API, useAuth } from "@/App";
import { ArrowLeft, Send, Loader2, CheckCircle2, MessageSquare } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent } from "@/components/ui/card";
import { toast } from "sonner";
import { motion } from "framer-motion";

const CATEGORIES = [
  { value: "bug", label: "Signaler un bug" },
  { value: "feature", label: "Suggestion de fonctionnalité" },
  { value: "account", label: "Problème de compte" },
  { value: "business", label: "Espace professionnel" },
  { value: "data", label: "Mes données (RGPD)" },
  { value: "other", label: "Autre" },
];

const SupportPage = () => {
  const { user, token } = useAuth();
  const [name, setName] = useState(user?.pseudo || "");
  const [email, setEmail] = useState(user?.email || "");
  const [category, setCategory] = useState("");
  const [subject, setSubject] = useState("");
  const [message, setMessage] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [ticketId, setTicketId] = useState(null);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!name.trim() || !email.trim() || !category || !subject.trim() || !message.trim()) {
      toast.error("Veuillez remplir tous les champs");
      return;
    }
    setSubmitting(true);
    try {
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      const res = await axios.post(`${API}/support/tickets`, {
        name: name.trim(),
        email: email.trim(),
        category,
        subject: subject.trim(),
        message: message.trim(),
      }, { headers });
      setTicketId(res.data.ticket_id);
      setSubmitted(true);
    } catch {
      toast.error("Erreur lors de l'envoi. Réessayez.");
    } finally {
      setSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <div className="px-4 py-12 max-w-md mx-auto text-center">
        <motion.div initial={{ scale: 0.8, opacity: 0 }} animate={{ scale: 1, opacity: 1 }}>
          <div className="w-20 h-20 mx-auto rounded-full bg-green-100 flex items-center justify-center mb-4">
            <CheckCircle2 className="w-10 h-10 text-green-600" />
          </div>
          <h1 className="font-heading text-2xl font-bold text-charcoal mb-2">Ticket envoyé !</h1>
          <p className="text-muted-foreground text-sm mb-2">Votre demande a bien été enregistrée.</p>
          {ticketId && (
            <p className="text-xs text-muted-foreground bg-gray-50 rounded-lg py-2 px-3 inline-block">
              Numéro de ticket : <strong className="text-charcoal">{ticketId}</strong>
            </p>
          )}
          <p className="text-sm text-muted-foreground mt-4">Notre équipe vous répondra sous 24h.</p>
          <Link to="/">
            <Button className="mt-6 rounded-full">Retour à l'accueil</Button>
          </Link>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="px-4 py-8 max-w-lg mx-auto">
      <Link to="/" className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-charcoal mb-6">
        <ArrowLeft className="w-4 h-4" /> Retour
      </Link>
      <div className="flex items-center gap-3 mb-6">
        <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center">
          <MessageSquare className="w-5 h-5 text-primary" />
        </div>
        <div>
          <h1 className="font-heading text-xl font-bold text-charcoal">Support technique</h1>
          <p className="text-xs text-muted-foreground">Notre équipe vous répondra sous 24h</p>
        </div>
      </div>

      <Card>
        <CardContent className="p-5">
          <form onSubmit={handleSubmit} className="space-y-4" data-testid="support-form">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-xs font-medium text-charcoal mb-1 block">Nom</label>
                <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="Votre nom" data-testid="support-name" />
              </div>
              <div>
                <label className="text-xs font-medium text-charcoal mb-1 block">Email</label>
                <Input type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="votre@email.fr" data-testid="support-email" />
              </div>
            </div>

            <div>
              <label className="text-xs font-medium text-charcoal mb-1 block">Catégorie</label>
              <select
                value={category}
                onChange={(e) => setCategory(e.target.value)}
                className="w-full h-10 rounded-md border border-input bg-background px-3 text-sm"
                data-testid="support-category"
              >
                <option value="">Sélectionnez une catégorie</option>
                {CATEGORIES.map((c) => (
                  <option key={c.value} value={c.value}>{c.label}</option>
                ))}
              </select>
            </div>

            <div>
              <label className="text-xs font-medium text-charcoal mb-1 block">Objet</label>
              <Input value={subject} onChange={(e) => setSubject(e.target.value)} placeholder="Résumé de votre demande" data-testid="support-subject" />
            </div>

            <div>
              <label className="text-xs font-medium text-charcoal mb-1 block">Message</label>
              <Textarea value={message} onChange={(e) => setMessage(e.target.value)} rows={5} placeholder="Décrivez votre problème en détail..." data-testid="support-message" />
            </div>

            <Button type="submit" disabled={submitting} className="w-full rounded-full h-11 gap-2" data-testid="support-submit">
              {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
              Envoyer le ticket
            </Button>
          </form>
        </CardContent>
      </Card>
    </div>
  );
};

export default SupportPage;
